import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Slider } from './ui/slider';
import { Clock, Euro, TrendingUp, Zap, Sparkles } from 'lucide-react';
import { FadeInWhenVisible } from './AnimatedUI';

const ROICalculator = () => {
  const [wohnungen, setWohnungen] = useState([50]);
  const [stundenlohn, setStundenlohn] = useState([55]);

  // Annahmen pro Wohnung
  const minutenSparungProWohnung = 35; // 35 Min Doku-Aufwand gespart
  const fehlerRateProzent = 12; // 12% Nacharbeit ohne Tool
  const nacharbeitKostenProFall = 180; // 180€ pro Nacharbeit-Fall

  const monatlicheWohnungen = wohnungen[0];
  const stundenkostenWert = stundenlohn[0];

  // Berechnungen
  const stundenGespart = Math.round((monatlicheWohnungen * minutenSparungProWohnung) / 60);
  const arbeitskostenErsparnis = stundenGespart * stundenkostenWert;
  const nacharbeitFaelle = Math.round((monatlicheWohnungen * fehlerRateProzent) / 100);
  const nacharbeitErsparnis = nacharbeitFaelle * nacharbeitKostenProFall;
  const gesamtErsparnisMonatlich = arbeitskostenErsparnis + nacharbeitErsparnis;
  const gesamtErsparnisJaehrlich = gesamtErsparnisMonatlich * 12;

  return (
    <section className="py-32 bg-gradient-to-br from-cyan-50 via-white to-blue-50 relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-20 right-20 w-96 h-96 bg-cyan-400/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-blue-400/20 rounded-full blur-[120px]" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeInWhenVisible>
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <Badge className="mb-4 bg-cyan-500/10 text-cyan-700 border-cyan-500/30">
              <Sparkles className="h-3 w-3 mr-2 inline" />
              ROI-Rechner
            </Badge>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
              Wieviel sparen Sie mit{' '}
              <span className="bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">
                AnschlussPilot?
              </span>
            </h2>
            <p className="text-xl text-gray-600">
              Berechnen Sie Ihre Ersparnis in 5 Sekunden.
            </p>
          </div>
        </FadeInWhenVisible>

        <FadeInWhenVisible delay={0.2}>
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Input Section */}
            <Card className="border-2 border-gray-100 bg-white/80 backdrop-blur-xl shadow-xl">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-8">Ihre Werte</h3>

                {/* Wohnungen Slider */}
                <div className="mb-10">
                  <div className="flex justify-between items-baseline mb-4">
                    <label className="text-gray-700 font-semibold">Wohnungen / Monat</label>
                    <span className="text-3xl font-bold bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">
                      {monatlicheWohnungen}
                    </span>
                  </div>
                  <Slider
                    value={wohnungen}
                    onValueChange={setWohnungen}
                    min={10}
                    max={500}
                    step={5}
                    className="mb-2"
                    data-testid="roi-wohnungen-slider"
                  />
                  <div className="flex justify-between text-xs text-gray-400">
                    <span>10</span>
                    <span>500</span>
                  </div>
                </div>

                {/* Stundenlohn Slider */}
                <div className="mb-8">
                  <div className="flex justify-between items-baseline mb-4">
                    <label className="text-gray-700 font-semibold">Ø Stundensatz Monteur</label>
                    <span className="text-3xl font-bold bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">
                      {stundenkostenWert} €
                    </span>
                  </div>
                  <Slider
                    value={stundenlohn}
                    onValueChange={setStundenlohn}
                    min={35}
                    max={120}
                    step={5}
                    className="mb-2"
                    data-testid="roi-stundenlohn-slider"
                  />
                  <div className="flex justify-between text-xs text-gray-400">
                    <span>35 €</span>
                    <span>120 €</span>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
                  <p className="text-xs text-gray-500 leading-relaxed">
                    <strong>Grundlage:</strong> ~35 Min Dokuersparnis pro Wohnung, durchschnittlich 12% Nacharbeit ohne Tool (180€ pro Fall). Konservative Schätzungen basierend auf Branchendaten.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Results Section */}
            <div className="space-y-4">
              {/* Big Number */}
              <motion.div
                key={gesamtErsparnisMonatlich}
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
              >
                <Card className="bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 border-0 shadow-2xl shadow-cyan-500/20 overflow-hidden relative">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/20 rounded-full blur-[80px]" />
                  <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/20 rounded-full blur-[80px]" />
                  <CardContent className="p-8 relative">
                    <p className="text-cyan-300 text-sm uppercase tracking-widest mb-3 font-semibold">Ihre Ersparnis pro Monat</p>
                    <div className="flex items-baseline mb-2">
                      <span className="text-6xl lg:text-7xl font-bold bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent">
                        {gesamtErsparnisMonatlich.toLocaleString('de-DE')}
                      </span>
                      <span className="text-3xl font-bold text-white ml-2">€</span>
                    </div>
                    <p className="text-gray-400 text-sm">
                      Hochgerechnet: <span className="text-cyan-300 font-bold">{gesamtErsparnisJaehrlich.toLocaleString('de-DE')} €</span> / Jahr
                    </p>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Breakdown */}
              <div className="grid grid-cols-2 gap-4">
                <motion.div
                  key={`time-${stundenGespart}`}
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.1 }}
                >
                  <Card className="border-2 border-gray-100 hover:border-cyan-300 transition-colors h-full">
                    <CardContent className="p-5">
                      <Clock className="h-8 w-8 text-cyan-500 mb-3" />
                      <p className="text-3xl font-bold text-gray-900 mb-1">{stundenGespart}h</p>
                      <p className="text-sm text-gray-500">Zeit gespart / Monat</p>
                    </CardContent>
                  </Card>
                </motion.div>

                <motion.div
                  key={`labor-${arbeitskostenErsparnis}`}
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.15 }}
                >
                  <Card className="border-2 border-gray-100 hover:border-cyan-300 transition-colors h-full">
                    <CardContent className="p-5">
                      <Euro className="h-8 w-8 text-emerald-500 mb-3" />
                      <p className="text-3xl font-bold text-gray-900 mb-1">
                        {arbeitskostenErsparnis.toLocaleString('de-DE')}€
                      </p>
                      <p className="text-sm text-gray-500">Arbeitskosten gespart</p>
                    </CardContent>
                  </Card>
                </motion.div>

                <motion.div
                  key={`rework-${nacharbeitFaelle}`}
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <Card className="border-2 border-gray-100 hover:border-cyan-300 transition-colors h-full">
                    <CardContent className="p-5">
                      <Zap className="h-8 w-8 text-orange-500 mb-3" />
                      <p className="text-3xl font-bold text-gray-900 mb-1">{nacharbeitFaelle}</p>
                      <p className="text-sm text-gray-500">Nacharbeiten vermieden</p>
                    </CardContent>
                  </Card>
                </motion.div>

                <motion.div
                  key={`rework-cost-${nacharbeitErsparnis}`}
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.25 }}
                >
                  <Card className="border-2 border-gray-100 hover:border-cyan-300 transition-colors h-full">
                    <CardContent className="p-5">
                      <TrendingUp className="h-8 w-8 text-blue-500 mb-3" />
                      <p className="text-3xl font-bold text-gray-900 mb-1">
                        {nacharbeitErsparnis.toLocaleString('de-DE')}€
                      </p>
                      <p className="text-sm text-gray-500">Nacharbeit-Kosten</p>
                    </CardContent>
                  </Card>
                </motion.div>
              </div>

              <a 
                href="#demo" 
                className="block text-center py-4 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white font-bold rounded-2xl shadow-xl shadow-cyan-500/30 transition-all hover:scale-[1.02]"
                data-testid="roi-cta-demo"
              >
                Demo anfragen und Ersparnis sichern →
              </a>
            </div>
          </div>
        </FadeInWhenVisible>
      </div>
    </section>
  );
};

export default ROICalculator;
